// ======================================================================
// File: PurchaseRequestViewDrawer.js
// Module: PPE → Procurement → Purchase Request
//
// Purpose:
// - View Purchase Request details
// - Approve / Reject Purchase Request (backend controlled)
// - Pattern identical to IndentApprovalDrawer
//
// Production Version (Debug Removed)
// ======================================================================

import React, { useEffect, useState } from "react";
import { formatDate } from "../../../../../../utils/formatUtils";

import purchaseRequestService from "../services/purchaseRequestService";

import PRLineTable from "../form/PRLineTable";
import PRStatusBadge from "../components/PRStatusBadge";
import PRTimeline from "../components/PRTimeline";

import "../pr-ui.css";

export default function PurchaseRequestViewDrawer({
  open,
  recordId,
  onClose,
  onUpdated,
}) {

  const [loading, setLoading] = useState(false);
  const [pr, setPr] = useState(null);

  // ==============================================================
  // LOAD PR
  // ==============================================================
  useEffect(() => {
    if (open && recordId) {
      loadPR();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordId]);

  const loadPR = async () => {
    try {
      setLoading(true);
      const res = await purchaseRequestService.getPurchaseRequestById(recordId);
      setPr(res?.data?.data);
    } catch (err) {
      console.error("Failed to load purchase request", err);
      alert("Failed to load purchase request details");
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // APPROVE
  // ==============================================================
  const handleApprove = async () => {
    if (!window.confirm("Approve this Purchase Request?")) return;

    try {
      setLoading(true);

      await purchaseRequestService.approvePurchaseRequest(recordId);

      alert("Purchase Request approved successfully");

      await loadPR();

      onUpdated?.();

    } catch (err) {
      console.error("Approval failed", err);
      alert("Failed to approve purchase request");
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
// CANCEL
// ==============================================================
const handleCancelPR = async () => {

  if (
    !window.confirm(
      "Cancel Purchase Request and release all blocked indent quantities?"
    )
  ) {
    return;
  }

  try {

    setLoading(true);

    await purchaseRequestService.cancelPurchaseRequest(
      recordId
    );

    alert("Purchase Request cancelled");

    await loadPR();

    onUpdated?.();

  } catch (err) {

    console.error("Cancel failed", err);

    alert(
      err?.response?.data?.message ||
      "Failed to cancel purchase request"
    );

  } finally {

    setLoading(false);

  }
};
  // ==============================================================
  // REJECT
  // ==============================================================
  const handleReject = async () => {
    if (!window.confirm("Reject this Purchase Request?")) return;

    try {
      setLoading(true);

      await purchaseRequestService.rejectPurchaseRequest(recordId);

      alert("Purchase Request rejected");

      await loadPR();

      onUpdated?.();

    } catch (err) {
      console.error("Reject failed", err);
      alert("Failed to reject purchase request");
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // RENDER
  // ==============================================================
  if (!open) return null;

  return (
    <div className="pr-drawer open">

      {/* HEADER */}
      <div className="pr-drawer-header">
        <h3>Purchase Request Details</h3>

        <button
          className="btn-secondary"
          onClick={onClose}
        >
          ✕
        </button>
        {pr &&
          ["DRAFT", "SUBMITTED"].includes(
            pr.status_code
          ) && (
            <button
              className="btn-warning"
              onClick={handleCancelPR}
              disabled={loading}
            >
              Cancel PR
            </button>
        )}
      </div>

      {/* BODY */}
      <div className="pr-drawer-body">

        {loading && (
          <div className="pr-empty-state">
            Loading purchase request...
          </div>
        )}

        {!loading && pr && (
          <>

            {/* HEADER CARD */}
<div
  className="pr-header-card"
  style={{
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr auto",
    gap: "20px",
    alignItems: "start",
  }}
>

  <div>
    <div
      style={{
        fontSize: "12px",
        color: "#6b7280",
        marginBottom: "4px",
      }}
    >
      PR Number
    </div>

    <div
      style={{
        fontWeight: 600,
        wordBreak: "break-word",
      }}
    >
      {pr.pr_no}
    </div>
  </div>

  <div>
    <div
      style={{
        fontSize: "12px",
        color: "#6b7280",
        marginBottom: "4px",
      }}
    >
      PR Date
    </div>

    <div>{formatDate(pr.pr_date)}</div>
  </div>

  <div>
    <div
      style={{
        fontSize: "12px",
        color: "#6b7280",
        marginBottom: "4px",
      }}
    >
      Department
    </div>

    <div>{pr.department_name || "-"}</div>
  </div>

  <div
    style={{
      justifySelf: "end",
    }}
  >
    <PRStatusBadge status={pr.status} />
  </div>

</div>

            {/* LINE ITEMS */}
            <div className="section-header">
              <h4>Materials</h4>
            </div>

            <PRLineTable
              prId={pr.id}
              lines={pr.lines || []}
            />

            {/* TIMELINE */}
            <PRTimeline history={pr.history || []} />

          </>
        )}

      </div>

      {/* FOOTER */}
      <div className="pr-drawer-footer">

        <button
          className="btn-secondary"
          onClick={onClose}
        >
          Close
        </button>

        {/* APPROVAL ACTIONS – ONLY WHEN PR IS SUBMITTED */}
        {pr && pr.status_code === "SUBMITTED" && (
          <>
            <button
              className="btn-danger"
              onClick={handleReject}
              disabled={loading}
            >
              Reject
            </button>

            <button
              className="btn-primary"
              onClick={handleApprove}
              disabled={loading}
            >
              Approve
            </button>
          </>
        )}

      </div>

    </div>
  );
}
